import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Chip,
    CircularProgress,
    MenuItem,
    Select,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useTheme, alpha } from '@mui/material/styles';
import { Badge, PanelHeader, SectionCard, StatBox } from '../components/shared';
import { PositionsTable } from '../components/PositionsTable';
import { TagBadge } from '../components/TagBadge';
import { TraderPnlChart } from '../components/charts/TraderPnlChart';

import {
    useTraderProfile,
    useTraderPnlSeries,
    useTraderPortfolioSeries,
    useTraderBalanceSeries,
    useTraderFlowSeries,
    useSimilarTraders,
    computeGlobalDomain,
} from '../api/useTraderProfile';
import type {
    ChartMode,
    FlowMode,
    PnlPoint,
    BalancePoint,
    FlowPoint,
    SimilarTrader,
} from '../api/useTraderProfile';
import { LiveTradesSidebar } from '../components/LiveTradesSidebar';
import { useHistoricalMarketTrades } from '../api/useHistoricalMarketTrades';
import { useAccountTradesStream } from '../../../hooks/useAccountTradesStream';
import { hoverScrollbar } from '../../../styles/hoverScrollbar';
import { TraderSubscribeBell } from '../../../notifications/TraderSubscribeBell';
import {
    shortAccount,
    cleanAccount,
    formatPnlShort,
    formatPct,
    formatPortfolio,
    formatCount,
} from '../utils/traderFormat';
import { toChecksumId } from '../utils/ids';
import type {
    TraderCategoryStats,
    PositionCategory,
} from '../api/traderProfileTypes';
import { POSITION_CATEGORIES } from '../api/traderProfileTypes';
import { CONTENT_HEIGHT_SX } from '../../layout/chrome';

// ─── Small SVG bar chart used for hourly activity and size distribution ──────

interface SvgBarChartProps {
    values: number[];
    labels: string[];
    // optional richer labels shown in hover tooltip; falls back to labels
    tooltipLabels?: string[];
    // label for the first tooltip row (e.g. "Time" or "Amount"); defaults to "Label"
    tooltipKeyLabel?: string;
    // label for the count row; defaults to "Count"
    tooltipCountLabel?: string;
    // optional per-bar color overrides; falls back to primary
    colors?: string[];
    height?: number;
}

function SvgBarChart({
    values,
    labels,
    tooltipLabels,
    tooltipKeyLabel = 'Label',
    tooltipCountLabel = 'Count',
    colors,
    height = 110,
}: SvgBarChartProps) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const max = Math.max(...values, 1);
    // Fixed viewBox width; SVG scales to container. SVG rects don't support CSS calc() for widths.
    const VW = 600;
    const LABEL_H = 22;
    const VH = height + LABEL_H;
    const barAreaH = height - 2;
    const count = values.length;
    const gap = 3;
    const barW = (VW - gap * (count - 1)) / count;
    const defaultColor = theme.palette.primary.main;
    const labelEvery = count > 12 ? 6 : count > 7 ? 3 : 1;
    // Estimated tooltip width for right-edge flip
    const TOOLTIP_W_BAR = 140;

    const containerRef = React.useRef<HTMLDivElement>(null);
    const [barHover, setBarHover] = useState<{
        label: string;
        value: number;
        x: number;
        y: number;
        flipLeft: boolean;
    } | null>(null);

    return (
        <Box ref={containerRef} sx={{ position: 'relative' }}>
            <svg
                viewBox={`0 0 ${VW} ${VH}`}
                preserveAspectRatio="none"
                width="100%"
                height={height + LABEL_H / 2}
                style={{ display: 'block', overflow: 'visible' }}
            >
                {values.map((v, i) => {
                    const barH = Math.max(2, (v / max) * barAreaH);
                    const x = i * (barW + gap);
                    const y = barAreaH - barH;
                    const color = colors?.[i] ?? defaultColor;
                    return (
                        <g key={i}>
                            <rect
                                x={x}
                                y={y}
                                width={barW}
                                height={barH}
                                rx={2}
                                fill={color}
                                opacity={0.85}
                                style={{ cursor: 'default' }}
                            />
                            {/* Full-height invisible overlay so tiny bars are hoverable */}
                            <rect
                                x={x}
                                y={0}
                                width={barW}
                                height={barAreaH}
                                fill="transparent"
                                style={{ cursor: 'default' }}
                                onMouseEnter={e => {
                                    const svgEl = (
                                        e.target as SVGElement
                                    ).closest('svg')!;
                                    const svgRect =
                                        svgEl.getBoundingClientRect();
                                    const containerRect =
                                        containerRef.current?.getBoundingClientRect();
                                    const scaleX = svgRect.width / VW;
                                    const scaleY = svgRect.height / VH;
                                    const absBarCenterX =
                                        svgRect.left + (x + barW / 2) * scaleX;
                                    const relX = containerRect
                                        ? absBarCenterX - containerRect.left
                                        : (x + barW / 2) * scaleX;
                                    const containerW =
                                        containerRect?.width ?? svgRect.width;
                                    setBarHover({
                                        label: (tooltipLabels ?? labels)[i],
                                        value: v,
                                        x: relX,
                                        y: y * scaleY,
                                        flipLeft:
                                            relX + TOOLTIP_W_BAR + 8 >
                                            containerW,
                                    });
                                }}
                                onMouseLeave={() => setBarHover(null)}
                            />
                            {i % labelEvery === 0 && (
                                <text
                                    x={x + barW / 2}
                                    y={VH - 4}
                                    textAnchor="middle"
                                    fontSize={18}
                                    fill={
                                        isDark
                                            ? 'rgba(255,255,255,0.45)'
                                            : 'rgba(0,0,0,0.45)'
                                    }
                                    fontFamily="inherit"
                                >
                                    {labels[i]}
                                </text>
                            )}
                        </g>
                    );
                })}
            </svg>
            {barHover && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: barHover.flipLeft
                            ? barHover.x - TOOLTIP_W_BAR - 4
                            : barHover.x + 6,
                        top: Math.max(0, barHover.y - 8),
                        pointerEvents: 'none',
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: '4px',
                        px: 0.75,
                        py: '4px',
                        zIndex: 20,
                        whiteSpace: 'nowrap',
                        width: TOOLTIP_W_BAR,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.4,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 1,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '10px',
                                color: 'text.disabled',
                                flexShrink: 0,
                            }}
                        >
                            {tooltipKeyLabel}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '10px',
                                color: 'text.secondary',
                                textAlign: 'right',
                            }}
                        >
                            {barHover.label}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 1,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '10px',
                                color: 'text.disabled',
                                flexShrink: 0,
                            }}
                        >
                            {tooltipCountLabel}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '11px',
                                fontFamily: 'monospace',
                                fontWeight: 600,
                                color: 'text.primary',
                            }}
                        >
                            {barHover.value}
                        </Typography>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
// ─── Category metrics row ─────────────────────────────────────────────────────

type CategoryMetricRow = TraderCategoryStats & { category: string };

type CategoryMetricKey =
    | 'total_pnl'
    | 'win_rate'
    | 'avg_edge'
    | 'profit_factor'
    | 'position_sharpe'
    | 'volume_usd'
    | 'market_count'
    | 'roct'
    | 'avg_hold_days'
    | 'market_liquidity_score';

interface CategoryMetricConfig {
    key: CategoryMetricKey;
    label: string;
    description: string;
    bidirectional: boolean;
    getValue: (s: CategoryMetricRow) => number | null;
    format: (v: number) => string;
}

// bidirectional metrics use the ±-centered bar (can go negative); the rest
// use a left-anchored bar. win_rate is already a 0..1 fraction so it doesn't
// need to be scaled against the visible set's max like the others do.
const CATEGORY_METRICS: CategoryMetricConfig[] = [
    {
        key: 'total_pnl',
        label: 'Total PnL',
        description:
            'Total profit and loss in this category, realized and unrealized.',
        bidirectional: true,
        getValue: s => s.total_pnl,
        format: v => fmtPnl(v),
    },
    {
        key: 'win_rate',
        label: 'Win rate',
        description:
            "Share of closed positions in this category that resolved in the trader's favor.",
        bidirectional: false,
        getValue: s => s.win_rate,
        format: v => `${(v * 100).toFixed(1)}%`,
    },
    {
        key: 'avg_edge',
        label: 'Average edge',
        description:
            'Average (resolution price − entry price) per share for closed positions in this category.',
        bidirectional: true,
        getValue: s => s.avg_edge,
        format: v => `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)}%`,
    },
    {
        key: 'profit_factor',
        label: 'Profit factor',
        description: 'Gross profit divided by gross loss in this category.',
        bidirectional: false,
        getValue: s => s.profit_factor,
        format: v => v.toFixed(2),
    },
    {
        key: 'position_sharpe',
        label: 'Position Sharpe ratio',
        description:
            'Risk-adjusted return across position-level returns in this category.',
        bidirectional: true,
        getValue: s => s.position_sharpe,
        format: v => `${v >= 0 ? '+' : ''}${v.toFixed(2)}`,
    },
    {
        key: 'volume_usd',
        label: 'Trading volume',
        description: 'Total USD volume traded in this category.',
        bidirectional: false,
        getValue: s => s.volume_usd,
        format: v => fmtUsd(v),
    },
    {
        key: 'market_count',
        label: 'Markets traded',
        description: 'Number of distinct markets traded in this category.',
        bidirectional: false,
        getValue: s => s.market_count,
        format: v => v.toFixed(0),
    },
    {
        key: 'roct',
        label: 'Return on capital-time',
        description: 'Return (pct) per $ per day in position.',
        bidirectional: true,
        getValue: s => s.roct,
        format: v => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`,
    },
    {
        key: 'avg_hold_days',
        label: 'Average hold time',
        description:
            'Average time between entry and exit for closed positions in this category.',
        bidirectional: false,
        getValue: s => s.avg_hold_days,
        format: v => fmtHoldDuration(v),
    },
    {
        key: 'market_liquidity_score',
        label: 'Market liquidity',
        description:
            'Money-weighted average liquidity of markets traded in this category (1.0 = deep, 0.0 = thin), based on trailing-24h volume.',
        bidirectional: false,
        getValue: s => s.market_liquidity_score,
        format: v => v.toFixed(2),
    },
];

const CATEGORY_METRIC_BY_KEY: Record<CategoryMetricKey, CategoryMetricConfig> =
    Object.fromEntries(CATEGORY_METRICS.map(m => [m.key, m])) as Record<
        CategoryMetricKey,
        CategoryMetricConfig
    >;

function CategoryRow({
    stat,
    metric,
    maxAbs,
}: {
    stat: CategoryMetricRow;
    metric: CategoryMetricKey;
    maxAbs: number;
}) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const trackBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
    const dividerColor = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)';

    const config = CATEGORY_METRIC_BY_KEY[metric];
    const rawValue = config.getValue(stat);
    const value = rawValue ?? 0;
    const isPos = value >= 0;
    const isRoi = config.bidirectional;

    const barFraction =
        metric === 'win_rate'
            ? Math.max(0, Math.min(1, value))
            : maxAbs > 0
              ? Math.abs(value) / maxAbs
              : 0;

    const barColor = (() => {
        if (metric === 'win_rate') {
            if (value >= 0.51) return theme.palette.success.main;
            if (value >= 0.49) return theme.palette.warning.main;
            return theme.palette.error.main;
        }
        if (config.bidirectional) {
            return isPos
                ? theme.palette.success.main
                : theme.palette.error.main;
        }
        return theme.palette.primary.main;
    })();

    const valueLabel = rawValue != null ? config.format(rawValue) : '—';

    const valueColor = (() => {
        if (rawValue == null) return 'text.disabled';
        if (metric === 'win_rate') {
            if (value >= 0.51) return 'success.main';
            if (value >= 0.49) return 'warning.main';
            return 'error.main';
        }
        if (config.bidirectional) {
            return isPos ? 'success.main' : 'error.main';
        }
        return 'text.primary';
    })();

    const tooltipText =
        metric === 'market_liquidity_score' && rawValue != null
            ? `${config.description} Typical market here trades roughly ${fmtUsd(impliedDailyVolumeFromLiquidity(rawValue))}/day.`
            : config.description;

    return (
        <Tooltip title={tooltipText} placement="left" arrow>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography
                    sx={{
                        fontSize: '11px',
                        color: 'text.secondary',
                        minWidth: 86,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                    }}
                >
                    {stat.category}
                </Typography>

                {isRoi ? (
                    // Bidirectional bar for signed metrics (roct, pnl, edge, sharpe)
                    <Box
                        sx={{
                            flex: 1,
                            height: 7,
                            display: 'flex',
                            position: 'relative',
                            borderRadius: '3px',
                            bgcolor: trackBg,
                        }}
                    >
                        {/* Left half (negative side) */}
                        <Box
                            sx={{
                                flex: 1,
                                position: 'relative',
                                overflow: 'hidden',
                                borderTopLeftRadius: '3px',
                                borderBottomLeftRadius: '3px',
                            }}
                        >
                            {!isPos && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        right: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: `${barFraction * 100}%`,
                                        bgcolor: barColor,
                                        opacity: 0.85,
                                        borderTopLeftRadius: '3px',
                                        borderBottomLeftRadius: '3px',
                                    }}
                                />
                            )}
                        </Box>
                        {/* Center divider */}
                        <Box
                            sx={{
                                width: '1px',
                                flexShrink: 0,
                                bgcolor: dividerColor,
                                zIndex: 1,
                            }}
                        />
                        {/* Right half (positive side) */}
                        <Box
                            sx={{
                                flex: 1,
                                position: 'relative',
                                overflow: 'hidden',
                                borderTopRightRadius: '3px',
                                borderBottomRightRadius: '3px',
                            }}
                        >
                            {isPos && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: `${barFraction * 100}%`,
                                        bgcolor: barColor,
                                        opacity: 0.85,
                                        borderTopRightRadius: '3px',
                                        borderBottomRightRadius: '3px',
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                ) : (
                    // Left-anchored bar for non-bidirectional metrics
                    <Box
                        sx={{
                            flex: 1,
                            height: 7,
                            borderRadius: '3px',
                            bgcolor: trackBg,
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                width: `${barFraction * 100}%`,
                                height: '100%',
                                bgcolor: barColor,
                                opacity: 0.85,
                                borderRadius: '3px',
                            }}
                        />
                    </Box>
                )}

                <Typography
                    sx={{
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        color: valueColor,
                        minWidth: 56,
                        textAlign: 'right',
                        flexShrink: 0,
                    }}
                >
                    {valueLabel}
                </Typography>
            </Box>
        </Tooltip>
    );
}

// ─── Behavior signal row ──────────────────────────────────────────────────────

function SignalRow({
    label,
    value,
    tooltip,
    neutral = false,
}: {
    label: string;
    value: number;
    tooltip: string;
    neutral?: boolean;
}) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const pct = Math.round(value * 100);

    const barColor = neutral
        ? theme.palette.primary.main
        : pct < 35
          ? theme.palette.success.main
          : pct < 60
            ? theme.palette.warning.main
            : theme.palette.error.main;

    return (
        <Tooltip title={tooltip} placement="left" arrow>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1.25,
                    cursor: 'default',
                }}
            >
                <Typography
                    sx={{
                        fontSize: '11px',
                        color: 'text.secondary',
                        minWidth: 114,
                        flexShrink: 0,
                    }}
                >
                    {label}
                </Typography>
                <Box
                    sx={{
                        flex: 1,
                        height: 5,
                        bgcolor: isDark
                            ? 'rgba(255,255,255,0.08)'
                            : 'rgba(0,0,0,0.07)',
                        borderRadius: '3px',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            width: `${pct}%`,
                            height: '100%',
                            bgcolor: barColor,
                            borderRadius: '3px',
                        }}
                    />
                </Box>
                <Typography
                    sx={{
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        color: barColor,
                        minWidth: 48,
                        textAlign: 'right',
                        flexShrink: 0,
                    }}
                >
                    {pct}%
                </Typography>
            </Box>
        </Tooltip>
    );
}

// ─── Avg-edge row — bidirectional centered bar, ±¢ label ─────────────────────

function EdgeRow({ value, tooltip }: { value: number; tooltip: string }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const MAX = 0.2;
    const fraction = Math.min(Math.abs(value) / MAX, 1);
    const isPos = value >= 0;
    const barColor = isPos
        ? theme.palette.success.main
        : theme.palette.error.main;
    const dividerColor = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)';
    const trackBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
    const label = `${isPos ? '+' : ''}${(value * 100).toFixed(1)}%`;

    return (
        <Tooltip title={tooltip} placement="left" arrow>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1.25,
                    cursor: 'default',
                }}
            >
                <Typography
                    sx={{
                        fontSize: '11px',
                        color: 'text.secondary',
                        minWidth: 114,
                        flexShrink: 0,
                    }}
                >
                    Avg edge
                </Typography>
                <Box
                    sx={{
                        flex: 1,
                        height: 5,
                        display: 'flex',
                        position: 'relative',
                        borderRadius: '3px',
                        bgcolor: trackBg,
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            position: 'relative',
                            overflow: 'hidden',
                            borderTopLeftRadius: '3px',
                            borderBottomLeftRadius: '3px',
                        }}
                    >
                        {!isPos && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    right: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: `${fraction * 100}%`,
                                    bgcolor: barColor,
                                    opacity: 0.85,
                                    borderTopLeftRadius: '3px',
                                    borderBottomLeftRadius: '3px',
                                }}
                            />
                        )}
                    </Box>
                    <Box
                        sx={{
                            width: '1px',
                            flexShrink: 0,
                            bgcolor: dividerColor,
                            zIndex: 1,
                        }}
                    />
                    <Box
                        sx={{
                            flex: 1,
                            position: 'relative',
                            overflow: 'hidden',
                            borderTopRightRadius: '3px',
                            borderBottomRightRadius: '3px',
                        }}
                    >
                        {isPos && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: `${fraction * 100}%`,
                                    bgcolor: barColor,
                                    opacity: 0.85,
                                    borderTopRightRadius: '3px',
                                    borderBottomRightRadius: '3px',
                                }}
                            />
                        )}
                    </Box>
                </Box>
                <Typography
                    sx={{
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        color: barColor,
                        minWidth: 48,
                        textAlign: 'right',
                        flexShrink: 0,
                    }}
                >
                    {label}
                </Typography>
            </Box>
        </Tooltip>
    );
}

// ─── Entry-prob row ───────────────────────────────────────────────────────────

function EntryProbRow({
    avgEntryProb,
    buckets,
    tooltip,
}: {
    avgEntryProb: number;
    buckets: number[];
    tooltip: string;
}) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const trackBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
    const bucketColor = theme.palette.primary.main;
    const needleColor = theme.palette.text.primary;

    const needlePct = Math.min(Math.max(avgEntryProb, 0), 1) * 100;
    const label = `${(avgEntryProb * 100).toFixed(1)}%`;

    return (
        <Tooltip title={tooltip} placement="left" arrow>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1.25,
                    cursor: 'default',
                }}
            >
                <Typography
                    sx={{
                        fontSize: '11px',
                        color: 'text.secondary',
                        minWidth: 114,
                        flexShrink: 0,
                    }}
                >
                    Avg entry prob
                </Typography>
                <Box
                    sx={{
                        flex: 1,
                        position: 'relative',
                        height: 5,
                        borderRadius: '3px',
                        bgcolor: trackBg,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{ position: 'absolute', inset: 0, display: 'flex' }}
                    >
                        {buckets.map((frac, i) => (
                            <Box
                                key={i}
                                sx={{
                                    flex: 1,
                                    height: '100%',
                                    bgcolor: alpha(
                                        bucketColor,
                                        0.1 + Math.min(frac, 0.5) * 1.6
                                    ),
                                    borderRight:
                                        i < 9
                                            ? `1px solid ${isDark ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.5)'}`
                                            : 'none',
                                }}
                            />
                        ))}
                    </Box>
                    <Box
                        sx={{
                            position: 'absolute',
                            left: `${needlePct}%`,
                            top: 0,
                            bottom: 0,
                            width: '2px',
                            bgcolor: needleColor,
                            transform: 'translateX(-50%)',
                            zIndex: 2,
                            opacity: 0.9,
                        }}
                    />
                </Box>
                <Typography
                    sx={{
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        color: bucketColor,
                        minWidth: 48,
                        textAlign: 'right',
                        flexShrink: 0,
                    }}
                >
                    {label}
                </Typography>
            </Box>
        </Tooltip>
    );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtUsd(v: number): string {
    if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
    return `$${v.toFixed(0)}`;
}

function fmtPnl(v: number): string {
    return (v >= 0 ? '+' : '') + fmtUsd(v);
}

function fmtHoldDuration(days: number): string {
    const totalMinutes = days * 24 * 60;
    if (totalMinutes < 1) return `${(totalMinutes * 60).toFixed(0)}s`;
    if (totalMinutes < 60) return `${totalMinutes.toFixed(1)}m`;
    const hours = days * 24;
    if (hours < 24) return `${hours.toFixed(2)}h`;
    return `${days.toFixed(2)}d`;
}

// Inverse of the backend's liquidity_score_from_volume: maps a [0,1] score
// back to an implied trailing-24h USD volume, for display purposes only.
// Same floor/saturation constants as LIQ_VOL_FLOOR_USD / LIQ_VOL_SATURATION_USD.
const LIQ_VOL_FLOOR_USD = 100.0;
const LIQ_VOL_SATURATION_USD = 100_000_000.0;

function impliedDailyVolumeFromLiquidity(score: number): number {
    const lo = Math.log(1 + LIQ_VOL_FLOOR_USD);
    const hi = Math.log(1 + LIQ_VOL_SATURATION_USD);
    const clamped = Math.max(0, Math.min(1, score));
    return Math.exp(lo + clamped * (hi - lo)) - 1;
}

function CopyableAddress({
    address,
    hasAlias,
}: {
    address: string;
    hasAlias: boolean;
}) {
    const [copied, setCopied] = useState(false);

    function handleCopy() {
        navigator.clipboard.writeText(address).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    }

    return (
        <Tooltip
            title={copied ? 'Copied!' : 'Click to copy'}
            placement="bottom"
            arrow
        >
            <Typography
                onClick={handleCopy}
                sx={{
                    fontFamily: 'monospace',
                    fontSize: { xs: '12px', md: hasAlias ? '12px' : '15px' },
                    fontWeight: hasAlias ? 400 : 800,
                    color: hasAlias ? 'text.disabled' : 'text.primary',
                    cursor: 'pointer',
                    userSelect: 'none',
                    letterSpacing: hasAlias ? undefined : '0.01em',
                    transition: 'color 0.15s',
                    '&:hover': { color: 'primary.main' },
                }}
            >
                <Box
                    component="span"
                    sx={{ display: { xs: 'inline', md: 'none' } }}
                >
                    {address}
                </Box>
                <Box
                    component="span"
                    sx={{ display: { xs: 'none', md: 'inline' } }}
                >
                    {address}
                </Box>
            </Typography>
        </Tooltip>
    );
}

// ─── External profile links ───────────────────────────────────────────────────

function PolymarketIcon() {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.1"
            strokeLinejoin="round"
            strokeLinecap="round"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path d="M20 3 4 8l16 3.7L4 15l16 6" />
            <path d="M20 3v18" />
            <path d="M4 8v7" />
        </svg>
    );
}

function PolygonscanIcon() {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M16.6 8.5 20.4 10.7a1.2 1.2 0 0 1 .6 1v4.4a1.2 1.2 0 0 1-.6 1l-3.8 2.2a1.2 1.2 0 0 1-1.2 0l-3.8-2.2a1.2 1.2 0 0 1-.6-1v-7.8L7.7 6.8 4.6 8.6v3.6l3.1 1.8 1.6-.9v2.1l-1 .6a1.2 1.2 0 0 1-1.2 0L3.3 13.6a1.2 1.2 0 0 1-.6-1V8.2a1.2 1.2 0 0 1 .6-1l3.8-2.2a1.2 1.2 0 0 1 1.2 0l3.8 2.2a1.2 1.2 0 0 1 .6 1V16l2.9 1.7 3.1-1.8v-3.6l-3.1-1.8-1.6.9V9.3l1-.6a1.2 1.2 0 0 1 1.2 0Z"
                fill="currentColor"
                transform="scale(-1,1) translate(-24,0)"
            />
        </svg>
    );
}

function ExternalIconLink({
    href,
    title,
    children,
}: {
    href: string;
    title: string;
    children: React.ReactNode;
}) {
    return (
        <Tooltip title={title} arrow>
            <Box
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={title}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    borderRadius: '6px',
                    color: 'text.secondary',
                    border: theme => `1px solid ${theme.palette.divider}`,
                    transition: 'color 0.15s, border-color 0.15s',
                    '&:hover': {
                        color: 'primary.main',
                        borderColor: 'primary.main',
                    },
                }}
            >
                {children}
            </Box>
        </Tooltip>
    );
}

// ─── Similar traders panel ────────────────────────────────────────────────────

const SIMILAR_COLUMNS: {
    key: string;
    label: string;
    align: 'left' | 'right';
    width?: number;
}[] = [
    { key: 'address', label: 'Address', align: 'left', width: 300 },
    { key: 'tags', label: 'Tags', align: 'left' },
    { key: 'portfolio', label: 'Portfolio', align: 'right', width: 90 },
    { key: 'pnl', label: 'PNL', align: 'right', width: 80 },
    { key: 'win_rate', label: 'Win rate', align: 'right', width: 80 },
    { key: 'avg_edge', label: 'Avg edge', align: 'right', width: 80 },
    { key: 'trades', label: 'Trades', align: 'right', width: 70 },
    { key: 'markets', label: 'Markets', align: 'right', width: 70 },
    { key: 'similarity', label: 'Similarity', align: 'left', width: 140 },
];

const statCellSx = {
    py: 0.75,
    fontSize: '11px',
    fontFamily: 'monospace',
    whiteSpace: 'nowrap',
} as const;

function SimBar({ value }: { value: number }) {
    const theme = useTheme();
    const pct = Math.max(0, Math.min(1, (value + 1) / 2)) * 100;
    const color =
        pct >= 75
            ? theme.palette.success.main
            : pct >= 50
              ? theme.palette.primary.main
              : theme.palette.text.disabled;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                minWidth: 80,
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    height: 4,
                    borderRadius: '2px',
                    bgcolor:
                        theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.07)'
                            : 'rgba(0,0,0,0.07)',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        width: `${pct}%`,
                        height: '100%',
                        bgcolor: color,
                        borderRadius: '2px',
                    }}
                />
            </Box>
            <Typography
                sx={{
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    color,
                    minWidth: 32,
                    textAlign: 'right',
                    flexShrink: 0,
                }}
            >
                {(value * 100).toFixed(0)}%
            </Typography>
        </Box>
    );
}

function SimilarTradersPanel({ address }: { address: string | undefined }) {
    const navigate = useNavigate();
    const theme = useTheme();

    const { data, loading, error } = useSimilarTraders(address, 20);

    return (
        <SectionCard sx={{ mb: 2 }}>
            <PanelHeader title="Similar traders" icon="🔍" />

            {loading ? (
                <Box sx={{ p: 1.5 }}>
                    {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} height={32} sx={{ mb: 0.5 }} />
                    ))}
                </Box>
            ) : error ? (
                <Typography
                    sx={{ p: 1.5, fontSize: '12px', color: 'error.main' }}
                >
                    Failed to load similar traders: {error}
                </Typography>
            ) : data && data.length > 0 ? (
                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 980 }}>
                        <TableHead>
                            <TableRow>
                                {SIMILAR_COLUMNS.map(col => (
                                    <TableCell
                                        key={col.key}
                                        align={col.align}
                                        sx={{
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            whiteSpace: 'nowrap',
                                            width: col.width,
                                            color:
                                                col.key === 'similarity'
                                                    ? 'primary.main'
                                                    : 'text.secondary',
                                        }}
                                    >
                                        {col.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((trader: SimilarTrader) => (
                                <TableRow
                                    key={trader.address}
                                    hover
                                    sx={{
                                        '&:last-child td': { border: 0 },
                                    }}
                                >
                                    <TableCell
                                        sx={{
                                            py: 0.75,
                                            width: 300,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        <Typography
                                            onClick={() =>
                                                navigate(
                                                    `/markets/trader/${trader.address}`
                                                )
                                            }
                                            sx={{
                                                fontSize: '12px',
                                                fontFamily: 'monospace',
                                                color: 'primary.main',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    textDecoration: 'underline',
                                                },
                                            }}
                                        >
                                            {trader.address}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 0.75 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                        >
                                            {trader.tags.length === 0 ? (
                                                <Typography
                                                    sx={{
                                                        fontSize: '11px',
                                                        color: 'text.disabled',
                                                    }}
                                                >
                                                    —
                                                </Typography>
                                            ) : (
                                                trader.tags.map(tag => (
                                                    <TagBadge
                                                        key={tag}
                                                        tag={tag}
                                                        dataIssues={
                                                            trader.data_issues
                                                        }
                                                    />
                                                ))
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right" sx={statCellSx}>
                                        {formatPortfolio(
                                            trader.portfolio_value
                                        )}
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{
                                            ...statCellSx,
                                            color:
                                                trader.total_pnl >= 0
                                                    ? theme.palette.success.main
                                                    : theme.palette.error.main,
                                        }}
                                    >
                                        {formatPnlShort(trader.total_pnl)}
                                    </TableCell>
                                    <TableCell align="right" sx={statCellSx}>
                                        {formatPct(trader.win_rate)}
                                    </TableCell>
                                    <TableCell align="right" sx={statCellSx}>
                                        {formatPct(trader.avg_edge)}
                                    </TableCell>
                                    <TableCell align="right" sx={statCellSx}>
                                        {formatCount(trader.num_trades)}
                                    </TableCell>
                                    <TableCell align="right" sx={statCellSx}>
                                        {formatCount(trader.num_markets)}
                                    </TableCell>
                                    <TableCell sx={{ py: 0.75, width: 140 }}>
                                        <SimBar value={trader.similarity} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            ) : (
                <Typography
                    sx={{ p: 1.5, fontSize: '12px', color: 'text.disabled' }}
                >
                    No similar traders found — embeddings may not yet cover this
                    address.
                </Typography>
            )}
        </SectionCard>
    );
}

// ─── Main view ────────────────────────────────────────────────────────────────

export function TraderView() {
    const { address } = useParams<{ address: string }>();
    const navigate = useNavigate();
    const { data: profile, loading, error } = useTraderProfile(address);
    const theme = useTheme();

    const tags = profile?.tags ?? [];

    // Historical trades for sidebar (account mode — all trades for this trader)
    const accountIdParam = address ?? undefined;
    const {
        trades: historicalTrades,
        loading: loadingHistorical,
        hasMore: hasMoreHistorical,
        error: historicalError,
        loadPage: loadMoreTrades,
        marketNames,
        resolveMarketNames,
    } = useHistoricalMarketTrades({
        accountId: accountIdParam,
        fromDate: profile?.activity.first_seen,
    });

    useEffect(() => {
        if (address) loadMoreTrades();
    }, [address, loadMoreTrades]);

    // Live RTDB trades for this account
    // Canonicalize to EIP-55 before it reaches the stream: the uid-scoped RTDB node
    // is `polymarket_account_trades/{uid}/{account}`, and bogg matches the account
    // segment by exact string. A lowercase URL would read a node nothing writes.
    const accountIdsParam = useMemo(
        () => (address ? [toChecksumId(address)] : []),
        [address]
    );
    const { trades: accountLiveTrades, connected: accountConnected } =
        useAccountTradesStream(accountIdsParam);

    // Live trades stream immediately and may reference markets the trader hasn't
    // traded in the loaded historical pages, so their condition_ids are absent
    // from marketNames (built only from historical trades). Resolve those names
    // too, otherwise live rows fall back to the truncated condition id.
    const liveMarketIds = useMemo(() => {
        const ids = new Set<string>();
        for (const [, trades] of accountLiveTrades) {
            for (const t of trades) {
                if (t.market_id) ids.add(t.market_id);
            }
        }
        return [...ids];
    }, [accountLiveTrades]);

    useEffect(() => {
        if (liveMarketIds.length > 0) resolveMarketNames(liveMarketIds);
    }, [liveMarketIds, resolveMarketNames]);

    const tradeMarkets = useMemo(
        () =>
            Object.entries(marketNames).map(([id, m]) => ({
                condition_id: id,
                question: m.question,
            })),
        [marketNames]
    );

    const getMarketHref = useCallback(
        (conditionId: string, hasMetadata: boolean) => {
            if (hasMetadata) {
                const m = marketNames[conditionId];
                if (m?.event_slug)
                    return `/markets/event/${m.event_slug}?markets=${conditionId}`;
            }
            return undefined;
        },
        [marketNames]
    );

    const MAX_CATEGORIES = 8;

    const [categoryMetric, setCategoryMetric] =
        useState<CategoryMetricKey>('total_pnl');

    // Always sorted by volume regardless of the selected metric, so the row
    // order doesn't jump around every time the dropdown selection changes.
    const { visibleCategoryStats, maxAbs } = useMemo(() => {
        const entries: CategoryMetricRow[] = Object.entries(
            profile?.category_metrics ?? {}
        ).map(([category, stats]) => ({ category, ...stats }));
        const sorted = entries
            .slice()
            .sort((a, b) => b.volume_usd - a.volume_usd);
        const sliced = sorted.slice(0, MAX_CATEGORIES);

        const config = CATEGORY_METRIC_BY_KEY[categoryMetric];
        const maxAbsValue = Math.max(
            ...sliced.map(s => Math.abs(config.getValue(s) ?? 0)),
            0.0001
        );

        return { visibleCategoryStats: sliced, maxAbs: maxAbsValue };
    }, [profile, categoryMetric]);

    // Full set of categories the trader has activity in — not limited to the
    // top MAX_CATEGORIES shown in the breakdown panel above.
    const availableCategories = useMemo<PositionCategory[]>(() => {
        const keys = Object.keys(profile?.category_metrics ?? {});
        return POSITION_CATEGORIES.filter(c => keys.includes(c));
    }, [profile]);

    // ── PnL series — always on ──
    const { data: pnlPoints, loading: loadingPnl } =
        useTraderPnlSeries(address);

    // ── Portfolio and balance series — always on so globalDomain covers all three ──
    const { data: portfolioPoints, loading: loadingPortfolio } =
        useTraderPortfolioSeries(address);

    const { data: balancePoints, loading: loadingBalance } =
        useTraderBalanceSeries(address);

    // ── Flow series — always on, shared across all modes ──
    const { data: flowPoints, loading: loadingFlows } =
        useTraderFlowSeries(address);

    // ── Zoom state ──
    const [zoomDomain, setZoomDomain] = useState<[Date, Date] | null>(null);
    const handleZoom = useCallback((d: [Date, Date]) => setZoomDomain(d), []);

    // ── Positions time-window filter, driven by the chart zoom ──
    const [heldWindowOff, setHeldWindowOff] = useState(false);

    // a new zoom re-applies the filter, even after the user removed it
    useEffect(() => {
        setHeldWindowOff(false);
    }, [zoomDomain]);

    const heldWindowActive = zoomDomain !== null && !heldWindowOff;

    const heldWindowLabel = useMemo(() => {
        if (!zoomDomain) return '';
        const fmt = (d: Date) =>
            `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
        return `Held ${fmt(zoomDomain[0])} – ${fmt(zoomDomain[1])}`;
    }, [zoomDomain]);

    // ── Chart mode ──
    const [chartMode, setChartMode] = useState<ChartMode>('pnl');
    const handleChartModeChange = useCallback((mode: ChartMode) => {
        setChartMode(mode);
        // intentionally NOT resetting zoomDomain — the selected time range should
        // stay stable when switching between series
    }, []);

    // ── Global domain: full extent across all series, used as the default x-axis
    // range so switching modes never shifts the timeframe ──
    const globalDomain = useMemo(
        () =>
            computeGlobalDomain(
                pnlPoints,
                portfolioPoints,
                balancePoints,
                flowPoints
            ),
        [pnlPoints, portfolioPoints, balancePoints, flowPoints]
    );

    // ── Flow sub-chart mode — default: deposit / withdraw ──
    const [flowMode, setFlowMode] = useState<FlowMode>('deposit_withdraw');

    // Derive the active primary points based on current mode
    const activePrimaryPoints: PnlPoint[] | BalancePoint[] =
        chartMode === 'portfolio'
            ? ((portfolioPoints ?? []) as PnlPoint[])
            : chartMode === 'balance'
              ? ((balancePoints ?? []) as BalancePoint[])
              : ((pnlPoints ?? []) as PnlPoint[]);

    const loadingPrimary =
        chartMode === 'portfolio'
            ? loadingPortfolio
            : chartMode === 'balance'
              ? loadingBalance
              : loadingPnl;

    // Color bars by intensity for hourly chart
    const hourlyColors = useMemo(() => {
        if (!profile) return [];
        const max = Math.max(...profile.activity.hourly_activity, 1);
        return profile.activity.hourly_activity.map(v => {
            const frac = v / max;
            if (frac > 0.75) return theme.palette.error.main;
            if (frac > 0.45) return theme.palette.warning.main;
            return alpha(theme.palette.primary.main, 0.7);
        });
    }, [profile, theme]);

    const timingInsight = useMemo(() => {
        if (!profile) return '';
        const activity = profile.activity.hourly_activity;
        const total = activity.reduce((s, v) => s + v, 0);
        if (total === 0) return 'No trading activity recorded.';

        const activeHours = activity.filter(v => v > 0).length;

        if (activeHours <= 3) {
            const peakHours = activity
                .map((v, i) => ({ v, i }))
                .filter(x => x.v > 0)
                .map(x => `${String(x.i).padStart(2, '0')}:00`)
                .join(', ');
            return `Activity in only ${activeHours} hour slot${activeHours > 1 ? 's' : ''} (${peakHours} UTC) — consistent with scheduled or scripted execution.`;
        }

        if (activeHours >= 18) {
            return 'Activity spread across most hours of the day — consistent with automated or algorithmic trading.';
        }

        const weighted = activity
            .map((v, i) => ({ v, i }))
            .filter(x => x.v > 0);
        const angles = weighted.map(x => (x.i / 24) * 2 * Math.PI);
        const sinSum = angles.reduce(
            (s, a, idx) => s + Math.sin(a) * weighted[idx].v,
            0
        );
        const cosSum = angles.reduce(
            (s, a, idx) => s + Math.cos(a) * weighted[idx].v,
            0
        );
        const meanAngle = Math.atan2(sinSum / total, cosSum / total);
        const centerHour = ((meanAngle / (2 * Math.PI)) * 24 + 24) % 24;
        const centerStr =
            String(Math.round(centerHour) % 24).padStart(2, '0') + ':00';

        if (activeHours <= 12) {
            return `Trading concentrated in a ${activeHours}-hour window centered around ${centerStr} UTC — likely a manual trader.`;
        }
        return `Activity across ${activeHours} hours, centered around ${centerStr} UTC — possibly manual with extended hours or semi-automated.`;
    }, [profile]);

    const sizeInsight = useMemo(() => {
        if (!profile || profile.activity.size_distribution.length === 0)
            return '';
        const buckets = profile.activity.size_distribution;
        const total = buckets.reduce((s, b) => s + b.count, 0);
        if (total === 0) return 'No position data.';

        const sorted = [...buckets].sort((a, b) => b.count - a.count);
        const topBucket = sorted[0];
        const topFrac = topBucket.count / total;

        if (buckets.length === 1) {
            return `All ${total} position${total > 1 ? 's' : ''} in the ${topBucket.bucket} range.`;
        }

        if (topFrac >= 0.7) {
            return `${Math.round(topFrac * 100)}% of positions concentrated in the ${topBucket.bucket} bucket — strongly favors this size range.`;
        }

        const topTwo = sorted.slice(0, 2);
        const topTwoFrac = (topTwo[0].count + topTwo[1].count) / total;
        if (topTwoFrac >= 0.8) {
            return `Positions split mainly between ${topTwo[0].bucket} and ${topTwo[1].bucket} (${Math.round(topTwoFrac * 100)}% combined).`;
        }

        return `Positions distributed across ${buckets.length} size ranges — no dominant bucket.`;
    }, [profile]);

    const activeSince = profile
        ? new Date(profile.activity.first_seen).toLocaleDateString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
          })
        : '—';
    const lastSeen = profile
        ? new Date(profile.activity.last_seen).toLocaleDateString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
          })
        : '—';

    // Chip style helper for mode toggles
    const chipSx = (active: boolean) => ({
        height: 20,
        fontSize: '10px',
        fontWeight: active ? 700 : 400,
        bgcolor: active ? 'action.selected' : 'transparent',
        border: 1,
        borderColor: active ? 'primary.main' : 'divider',
        color: active ? 'primary.main' : 'text.disabled',
        cursor: 'pointer',
    });

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    lg: '320px 1fr auto',
                },
                mx: 'auto',
                ...CONTENT_HEIGHT_SX,
                overflow: 'hidden',
            }}
        >
            {/* Left sidebar — live trades */}
            <Box
                sx={{
                    display: { xs: 'none', lg: 'block' },
                    height: '100%',
                    overflowY: 'hidden',
                    overscrollBehavior: 'contain',
                    borderRight: 1,
                    borderColor: 'divider',
                }}
            >
                <LiveTradesSidebar
                    liveTrades={accountLiveTrades}
                    markets={tradeMarkets}
                    connected={accountConnected}
                    historicalTrades={historicalTrades}
                    loadMoreTrades={loadMoreTrades}
                    loadingMore={loadingHistorical}
                    hasMore={hasMoreHistorical}
                    error={historicalError}
                    getTraderHref={addr => `/markets/trader/${addr}`}
                    getMarketHref={getMarketHref}
                    disableTraderTooltip
                />
            </Box>

            {/* Center content */}
            <Box
                sx={{
                    px: 2,
                    py: 2,
                    minWidth: 0,
                    height: '100%',
                    ...hoverScrollbar,
                }}
            >
                {/* ── Header ── */}
                {loading ? (
                    <Box sx={{ mb: 2 }}>
                        <Skeleton width={280} height={28} />
                        <Skeleton width={420} height={20} sx={{ mt: 0.5 }} />
                    </Box>
                ) : error ? (
                    <Typography
                        sx={{ color: 'error.main', fontSize: '13px', mb: 2 }}
                    >
                        Failed to load trader profile: {error}
                    </Typography>
                ) : (
                    profile && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                mb: 2,
                                px: 0.5,
                                flexWrap: 'wrap',
                                gap: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                }}
                            >
                                {profile.profile_pic_url ? (
                                    <Box
                                        component="img"
                                        src={profile.profile_pic_url}
                                        alt={profile.alias ?? profile.address}
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: '50%',
                                            objectFit: 'cover',
                                            flexShrink: 0,
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: '50%',
                                            bgcolor: alpha(
                                                theme.palette.primary.main,
                                                0.12
                                            ),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '15px',
                                            fontWeight: 600,
                                            color: 'primary.main',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {(profile.alias ?? profile.address)
                                            .slice(0, 2)
                                            .toUpperCase()}
                                    </Box>
                                )}
                                <Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'baseline',
                                            gap: 1,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        {profile.alias && (
                                            <Typography
                                                sx={{
                                                    fontSize: '18px',
                                                    fontWeight: 800,
                                                }}
                                            >
                                                {profile.alias}
                                            </Typography>
                                        )}
                                        <CopyableAddress
                                            address={profile.address}
                                            hasAlias={!!profile.alias}
                                        />
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            mt: 0.5,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        {tags.map(tag => (
                                            <TagBadge
                                                key={tag}
                                                tag={tag}
                                                dataIssues={profile.data_issues}
                                            />
                                        ))}
                                        <TraderSubscribeBell
                                            address={profile.address}
                                            label={shortAccount(
                                                profile.address
                                            )}
                                        />
                                        <ExternalIconLink
                                            href={`https://polymarket.com/de/@${cleanAccount(profile.address)}`}
                                            title="Open on Polymarket"
                                        >
                                            <PolymarketIcon />
                                        </ExternalIconLink>
                                        <ExternalIconLink
                                            href={`https://polygonscan.com/address/${cleanAccount(profile.address)}`}
                                            title="Open on Polygonscan"
                                        >
                                            <PolygonscanIcon />
                                        </ExternalIconLink>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Headline stats */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 3,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography
                                        sx={{
                                            fontSize: '10.5px',
                                            color: 'text.secondary',
                                            fontWeight: 500,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em',
                                        }}
                                    >
                                        Active since
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            fontFamily: 'monospace',
                                        }}
                                    >
                                        {activeSince}
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography
                                        sx={{
                                            fontSize: '10.5px',
                                            color: 'text.secondary',
                                            fontWeight: 500,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em',
                                        }}
                                    >
                                        Last trade
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            fontFamily: 'monospace',
                                        }}
                                    >
                                        {lastSeen}
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography
                                        sx={{
                                            fontSize: '10.5px',
                                            color: 'text.secondary',
                                            fontWeight: 500,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em',
                                        }}
                                    >
                                        Total PnL
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            fontFamily: 'monospace',
                                            color:
                                                profile.performance.total_pnl >=
                                                0
                                                    ? 'success.main'
                                                    : 'error.main',
                                        }}
                                    >
                                        {fmtPnl(profile.performance.total_pnl)}
                                    </Typography>
                                </Box>
                                {profile.financials.portfolio_value != null && (
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography
                                            sx={{
                                                fontSize: '10.5px',
                                                color: 'text.secondary',
                                                fontWeight: 500,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.04em',
                                            }}
                                        >
                                            Portfolio
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 600,
                                                fontFamily: 'monospace',
                                            }}
                                        >
                                            {fmtUsd(
                                                profile.financials
                                                    .portfolio_value
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                                {profile.financials.balance_usd != null && (
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography
                                            sx={{
                                                fontSize: '10.5px',
                                                color: 'text.secondary',
                                                fontWeight: 500,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.04em',
                                            }}
                                        >
                                            Balance
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 600,
                                                fontFamily: 'monospace',
                                            }}
                                        >
                                            {fmtUsd(
                                                profile.financials.balance_usd
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                                {profile.financials.net_deposits != null && (
                                    <Tooltip
                                        title={
                                            profile.financials.total_deposits !=
                                                null &&
                                            profile.financials
                                                .total_withdrawals != null
                                                ? `Deposits ${fmtUsd(profile.financials.total_deposits)} · Withdrawals ${fmtUsd(profile.financials.total_withdrawals)}`
                                                : ''
                                        }
                                        placement="top"
                                        arrow
                                    >
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography
                                                sx={{
                                                    fontSize: '10.5px',
                                                    color: 'text.secondary',
                                                    fontWeight: 500,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.04em',
                                                }}
                                            >
                                                Net deposits
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: '14px',
                                                    fontWeight: 600,
                                                    fontFamily: 'monospace',
                                                    color:
                                                        profile.financials
                                                            .net_deposits >= 0
                                                            ? 'text.primary'
                                                            : 'error.main',
                                                }}
                                            >
                                                {profile.financials
                                                    .net_deposits >= 0
                                                    ? '+'
                                                    : ''}
                                                {fmtUsd(
                                                    profile.financials
                                                        .net_deposits
                                                )}
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                    )
                )}

                {/* ── Metric cards ── */}
                {loading ? (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(6, 1fr)',
                            gap: 1.25,
                            mb: 1.5,
                        }}
                    >
                        {[...Array(6)].map((_, i) => (
                            <Skeleton key={i} height={72} variant="rounded" />
                        ))}
                    </Box>
                ) : (
                    profile && (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns:
                                    'repeat(auto-fit, minmax(120px, 1fr))',
                                gap: 1.25,
                                mb: 1.5,
                            }}
                        >
                            {[
                                {
                                    icon: '📊',
                                    label: 'Total trades',
                                    value: profile.activity.total_trades.toLocaleString(),
                                    sub: `~${Math.round(profile.activity.total_trades / Math.max(1, (new Date(profile.activity.last_seen).getTime() - new Date(profile.activity.first_seen).getTime()) / (1000 * 60 * 60 * 24 * 7)))} /wk`,
                                    tooltip:
                                        'Total number of trades executed, all time.',
                                },
                                {
                                    icon: '🎯',
                                    label: 'Win rate',
                                    value: `${(profile.performance.win_rate * 100).toFixed(1)}%`,
                                    sub: '',
                                    tooltip:
                                        "Share of closed positions that resolved in the trader's favor.",
                                },
                                {
                                    icon: '💰',
                                    label: 'Avg size',
                                    value: fmtUsd(
                                        profile.activity.avg_size_usd
                                    ),
                                    sub: 'per trade',
                                    tooltip: 'Average USD size per trade.',
                                },
                                {
                                    icon: '📈',
                                    label: 'Volume',
                                    value: fmtUsd(
                                        profile.activity.lifetime_volume_usd
                                    ),
                                    sub: 'lifetime',
                                    tooltip:
                                        'Total USD volume traded, lifetime.',
                                },
                                {
                                    icon: '🗂️',
                                    label: 'Markets',
                                    value: profile.activity.unique_markets.toString(),
                                    sub: `${Object.keys(profile.category_metrics).length} categories`,
                                    tooltip:
                                        'Number of distinct markets traded, across all categories.',
                                },
                                {
                                    icon: '⏱️',
                                    label: 'Avg hold',
                                    value: fmtHoldDuration(
                                        profile.activity.avg_hold_days
                                    ),
                                    sub: 'per position',
                                    tooltip:
                                        'Average time between entry and exit for closed positions.',
                                },
                            ].map(m => (
                                <Tooltip
                                    key={m.label}
                                    title={m.tooltip}
                                    placement="top"
                                    arrow
                                >
                                    <Box
                                        sx={{
                                            bgcolor: 'action.hover',
                                            borderRadius: 1,
                                            px: 1.5,
                                            py: 1.25,
                                        }}
                                    >
                                        <StatBox
                                            icon={m.icon}
                                            label={m.label}
                                            value={m.value}
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: '10px',
                                                color: 'text.disabled',
                                                textAlign: 'center',
                                                mt: 0.25,
                                            }}
                                        >
                                            {m.sub}
                                        </Typography>
                                    </Box>
                                </Tooltip>
                            ))}

                            {/* ROI */}
                            <Tooltip
                                title="Total PnL as a percentage of capital invested."
                                placement="top"
                                arrow
                            >
                                <Box
                                    sx={{
                                        bgcolor: 'action.hover',
                                        borderRadius: 1,
                                        px: 1.5,
                                        py: 1.25,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '10px',
                                            color: 'text.secondary',
                                            mb: 0.25,
                                        }}
                                    >
                                        📈 ROI
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '16px',
                                            fontWeight: 700,
                                            fontFamily: 'monospace',
                                            color:
                                                profile.performance.roi_pct >= 0
                                                    ? 'success.main'
                                                    : 'error.main',
                                        }}
                                    >
                                        {profile.performance.roi_pct >= 0
                                            ? '+'
                                            : ''}
                                        {profile.performance.roi_pct.toFixed(1)}
                                        %
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '10px',
                                            color: 'text.disabled',
                                            mt: 0.25,
                                        }}
                                    >
                                        return on investment
                                    </Typography>
                                </Box>
                            </Tooltip>

                            {/* ROE */}
                            {profile.performance.roe_pct != null && (
                                <Tooltip
                                    title="Total PnL as a percentage of net equity (deposits net of withdrawals)."
                                    placement="top"
                                    arrow
                                >
                                    <Box
                                        sx={{
                                            bgcolor: 'action.hover',
                                            borderRadius: 1,
                                            px: 1.5,
                                            py: 1.25,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '10px',
                                                color: 'text.secondary',
                                                mb: 0.25,
                                            }}
                                        >
                                            📊 ROE
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '16px',
                                                fontWeight: 700,
                                                fontFamily: 'monospace',
                                                color:
                                                    profile.performance
                                                        .roe_pct >= 0
                                                        ? 'success.main'
                                                        : 'error.main',
                                            }}
                                        >
                                            {profile.performance.roe_pct >= 0
                                                ? '+'
                                                : ''}
                                            {profile.performance.roe_pct.toFixed(
                                                1
                                            )}
                                            %
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '10px',
                                                color: 'text.disabled',
                                                mt: 0.25,
                                            }}
                                        >
                                            return on equity
                                        </Typography>
                                    </Box>
                                </Tooltip>
                            )}

                            {/* Profit factor */}
                            <Tooltip
                                title="Gross profit divided by gross loss across all closed positions. Above 1.0 means more won than lost."
                                placement="top"
                                arrow
                            >
                                <Box
                                    sx={{
                                        bgcolor: 'action.hover',
                                        borderRadius: 1,
                                        px: 1.5,
                                        py: 1.25,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '10px',
                                            color: 'text.secondary',
                                            mb: 0.25,
                                        }}
                                    >
                                        ⚙️ Profit factor
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '16px',
                                            fontWeight: 700,
                                            fontFamily: 'monospace',
                                            color:
                                                profile.performance
                                                    .profit_factor >= 1
                                                    ? 'success.main'
                                                    : 'error.main',
                                        }}
                                    >
                                        {profile.performance.profit_factor.toFixed(
                                            2
                                        )}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '10px',
                                            color: 'text.disabled',
                                            mt: 0.25,
                                        }}
                                    >
                                        gross win / gross loss
                                    </Typography>
                                </Box>
                            </Tooltip>

                            {/* Combined Sharpe card */}
                            {(profile.performance.daily_sharpe != null ||
                                profile.performance.position_sharpe !=
                                    null) && (
                                <Tooltip
                                    title="Risk-adjusted return. Daily uses day-over-day PnL volatility; per-position uses the distribution of individual position returns."
                                    placement="top"
                                    arrow
                                >
                                    <Box
                                        sx={{
                                            bgcolor: 'action.hover',
                                            borderRadius: 1,
                                            px: 1.5,
                                            py: 1.25,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '10px',
                                                color: 'text.secondary',
                                                textAlign: 'center',
                                                mb: 0.75,
                                            }}
                                        >
                                            ⚡ Sharpe
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 0.5,
                                            }}
                                        >
                                            {profile.performance.daily_sharpe !=
                                                null && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent:
                                                            'space-between',
                                                        alignItems: 'baseline',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: '10px',
                                                            color: 'text.disabled',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        Daily
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '12px',
                                                            fontFamily:
                                                                'monospace',
                                                            fontWeight: 700,
                                                            color:
                                                                profile
                                                                    .performance
                                                                    .daily_sharpe >=
                                                                0
                                                                    ? 'success.main'
                                                                    : 'error.main',
                                                        }}
                                                    >
                                                        {profile.performance
                                                            .daily_sharpe >= 0
                                                            ? '+'
                                                            : ''}
                                                        {profile.performance.daily_sharpe.toFixed(
                                                            2
                                                        )}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {profile.performance
                                                .position_sharpe != null && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent:
                                                            'space-between',
                                                        alignItems: 'baseline',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: '10px',
                                                            color: 'text.disabled',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        Per position
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '12px',
                                                            fontFamily:
                                                                'monospace',
                                                            fontWeight: 700,
                                                            color:
                                                                profile
                                                                    .performance
                                                                    .position_sharpe >=
                                                                0
                                                                    ? 'success.main'
                                                                    : 'error.main',
                                                        }}
                                                    >
                                                        {profile.performance
                                                            .position_sharpe >=
                                                        0
                                                            ? '+'
                                                            : ''}
                                                        {profile.performance.position_sharpe.toFixed(
                                                            2
                                                        )}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Tooltip>
                            )}

                            {/* Combined Returns card — roct / twr_pct */}
                            <Tooltip
                                title={
                                    <>
                                        ROCT: Return on capital-time, measured
                                        in PnL per $ per day in the position.
                                        <br />
                                        TWR: time-weighted return, removing the
                                        effect of deposit/withdrawal timing.
                                    </>
                                }
                                placement="top"
                                arrow
                            >
                                <Box
                                    sx={{
                                        bgcolor: 'action.hover',
                                        borderRadius: 1,
                                        px: 1.5,
                                        py: 1.25,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '10px',
                                            color: 'text.secondary',
                                            textAlign: 'center',
                                            mb: 0.75,
                                        }}
                                    >
                                        📐 Returns
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'baseline',
                                                gap: 0.5,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: '10px',
                                                    color: 'text.disabled',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                ROCT
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: '12px',
                                                    fontFamily: 'monospace',
                                                    fontWeight: 700,
                                                    color:
                                                        profile.performance
                                                            .roct >= 0
                                                            ? 'success.main'
                                                            : 'error.main',
                                                }}
                                            >
                                                {profile.performance.roct >= 0
                                                    ? '+'
                                                    : ''}
                                                {profile.performance.roct.toFixed(
                                                    1
                                                )}
                                                %
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'baseline',
                                                gap: 0.5,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: '10px',
                                                    color: 'text.disabled',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                TWR
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: '12px',
                                                    fontFamily: 'monospace',
                                                    fontWeight: 700,
                                                    color:
                                                        profile.performance
                                                            .twr_pct >= 0
                                                            ? 'success.main'
                                                            : 'error.main',
                                                }}
                                            >
                                                {profile.performance.twr_pct >=
                                                0
                                                    ? '+'
                                                    : ''}
                                                {profile.performance.twr_pct.toFixed(
                                                    1
                                                )}
                                                %
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Tooltip>
                        </Box>
                    )
                )}

                {/* ── PnL chart + Behavior signals ── */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 320px' },
                        gap: 1.5,
                        mb: 1.5,
                        alignItems: 'stretch',
                        minHeight: 340,
                    }}
                >
                    <SectionCard
                        sx={{ display: 'flex', flexDirection: 'column' }}
                    >
                        <PanelHeader
                            title={
                                chartMode === 'balance'
                                    ? 'USDC Balance'
                                    : chartMode === 'portfolio'
                                      ? 'Portfolio Value'
                                      : 'Cumulative PnL'
                            }
                            icon={
                                chartMode === 'balance'
                                    ? '💵'
                                    : chartMode === 'portfolio'
                                      ? '📊'
                                      : '📈'
                            }
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                {zoomDomain && (
                                    <Chip
                                        label="Reset zoom"
                                        size="small"
                                        onDelete={() => setZoomDomain(null)}
                                        onClick={() => setZoomDomain(null)}
                                        sx={{
                                            height: 22,
                                            fontSize: '10px',
                                            fontWeight: 600,
                                            mr: 0.5,
                                            '& .MuiChip-deleteIcon': {
                                                fontSize: '14px',
                                            },
                                        }}
                                    />
                                )}

                                {/* ── Primary series toggles ── */}
                                <Chip
                                    label="PnL"
                                    size="small"
                                    onClick={() => handleChartModeChange('pnl')}
                                    sx={chipSx(chartMode === 'pnl')}
                                />
                                <Chip
                                    label={
                                        loadingPortfolio ? (
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                }}
                                            >
                                                Portfolio{' '}
                                                <CircularProgress
                                                    size={10}
                                                    sx={{ ml: 0.5 }}
                                                />
                                            </Box>
                                        ) : (
                                            'Portfolio'
                                        )
                                    }
                                    size="small"
                                    onClick={() =>
                                        handleChartModeChange('portfolio')
                                    }
                                    sx={chipSx(chartMode === 'portfolio')}
                                />
                                <Chip
                                    label={
                                        loadingBalance ? (
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                }}
                                            >
                                                Balance{' '}
                                                <CircularProgress
                                                    size={10}
                                                    sx={{ ml: 0.5 }}
                                                />
                                            </Box>
                                        ) : (
                                            'Balance'
                                        )
                                    }
                                    size="small"
                                    onClick={() =>
                                        handleChartModeChange('balance')
                                    }
                                    sx={chipSx(chartMode === 'balance')}
                                />

                                {/* ── Divider ── */}
                                <Box
                                    sx={{
                                        width: '1px',
                                        height: 14,
                                        bgcolor: 'divider',
                                        mx: 0.25,
                                    }}
                                />

                                {/* ── Flow sub-chart toggles ── */}
                                <Tooltip
                                    title="Show deposits / withdrawals in sub-chart"
                                    placement="top"
                                    arrow
                                >
                                    <Chip
                                        label="Dep/Wdw"
                                        size="small"
                                        onClick={() =>
                                            setFlowMode('deposit_withdraw')
                                        }
                                        sx={{
                                            ...chipSx(
                                                flowMode === 'deposit_withdraw'
                                            ),
                                            fontSize: '9px',
                                        }}
                                    />
                                </Tooltip>
                                <Tooltip
                                    title="Show buy / sell volume in sub-chart"
                                    placement="top"
                                    arrow
                                >
                                    <Chip
                                        label="Buy/Sell"
                                        size="small"
                                        onClick={() => setFlowMode('buy_sell')}
                                        sx={{
                                            ...chipSx(flowMode === 'buy_sell'),
                                            fontSize: '9px',
                                        }}
                                    />
                                </Tooltip>
                                <Tooltip
                                    title="Show inflow / outflow in sub-chart"
                                    placement="top"
                                    arrow
                                >
                                    <Chip
                                        label="In/Out"
                                        size="small"
                                        onClick={() =>
                                            setFlowMode('inflow_outflow')
                                        }
                                        sx={{
                                            ...chipSx(
                                                flowMode === 'inflow_outflow'
                                            ),
                                            fontSize: '9px',
                                        }}
                                    />
                                </Tooltip>
                            </Box>
                        </PanelHeader>

                        <Box sx={{ p: 1.5, flex: 1, minHeight: 0 }}>
                            {loadingPrimary ? (
                                <Skeleton
                                    height={200}
                                    variant="rectangular"
                                    sx={{ borderRadius: 1 }}
                                />
                            ) : (
                                <TraderPnlChart
                                    primaryPoints={activePrimaryPoints}
                                    flowPoints={
                                        (flowPoints ?? []) as FlowPoint[]
                                    }
                                    chartMode={chartMode}
                                    flowMode={flowMode}
                                    zoomDomain={zoomDomain}
                                    globalDomain={globalDomain}
                                    onZoom={handleZoom}
                                />
                            )}
                        </Box>
                    </SectionCard>

                    <SectionCard sx={{ height: '100%' }}>
                        <PanelHeader title="Behavior signals" icon="🔬" />
                        <Box sx={{ p: 1.5 }}>
                            {loading ? (
                                [...Array(5)].map((_, i) => (
                                    <Skeleton
                                        key={i}
                                        height={18}
                                        sx={{ mb: 1 }}
                                    />
                                ))
                            ) : profile ? (
                                <>
                                    <SignalRow
                                        label="Algo likelihood"
                                        value={profile.behavior.algo_likelihood}
                                        neutral
                                        tooltip="Based on inter-trade timing regularity (σ of gap between consecutive trades)"
                                    />
                                    <SignalRow
                                        label="Maker ratio"
                                        value={profile.behavior.maker_ratio}
                                        neutral
                                        tooltip="Fraction of fills where this trader was resting maker (limit orders). High = predominantly passive."
                                    />
                                    <SignalRow
                                        label="Repeat market rate"
                                        value={
                                            profile.behavior.repeat_market_rate
                                        }
                                        neutral
                                        tooltip="Fraction of traded markets visited more than once (low = market-agnostic)"
                                    />
                                    <EntryProbRow
                                        avgEntryProb={
                                            profile.behavior.avg_entry_prob
                                        }
                                        buckets={
                                            profile.behavior.entry_prob_buckets
                                        }
                                        tooltip="Volume-weighted average probability at time of entry. Background shows how volume is distributed across probability buckets."
                                    />
                                    <EdgeRow
                                        value={profile.behavior.avg_edge}
                                        tooltip="Average (resolution price − entry price) per share across all closed positions. Positive = systematically bought below resolution price."
                                    />
                                    {profile.behavior.capital_utilization !=
                                        null && (
                                        <SignalRow
                                            label="Capital deployed"
                                            value={
                                                profile.behavior
                                                    .capital_utilization
                                            }
                                            neutral
                                            tooltip="Fraction of total capital currently deployed in open positions."
                                        />
                                    )}
                                    {profile.behavior.market_liquidity_score !=
                                        null && (
                                        <SignalRow
                                            label="Market liquidity"
                                            value={
                                                profile.behavior
                                                    .market_liquidity_score
                                            }
                                            neutral
                                            tooltip={`Money-weighted average liquidity across markets traded (1.0 = deep, 0.0 = thin), based on trailing-24h volume. Typical market here trades roughly ${fmtUsd(impliedDailyVolumeFromLiquidity(profile.behavior.market_liquidity_score))}/day.`}
                                        />
                                    )}
                                    <Box
                                        sx={{
                                            mt: 1.5,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.75,
                                        }}
                                    >
                                        {profile.behavior_insight.map(
                                            (insight, idx) => (
                                                <Box
                                                    key={idx}
                                                    sx={{
                                                        p: 1.25,
                                                        bgcolor: alpha(
                                                            theme.palette
                                                                .primary.main,
                                                            0.07
                                                        ),
                                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: '11px',
                                                            fontWeight: 600,
                                                            color: 'text.primary',
                                                            mb: 0.25,
                                                        }}
                                                    >
                                                        {insight.title}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '11px',
                                                            color: 'text.secondary',
                                                            lineHeight: 1.55,
                                                        }}
                                                    >
                                                        {insight.body}
                                                    </Typography>
                                                </Box>
                                            )
                                        )}
                                    </Box>
                                </>
                            ) : null}
                        </Box>
                    </SectionCard>
                </Box>

                {/* ── Category breakdown + Hourly activity + Size distribution ── */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr 1fr' },
                        gap: 1.5,
                        mb: 1.5,
                    }}
                >
                    <SectionCard>
                        <PanelHeader title="Category breakdown" icon="🗂️">
                            <Select
                                value={categoryMetric}
                                onChange={(e: SelectChangeEvent) =>
                                    setCategoryMetric(
                                        e.target.value as CategoryMetricKey
                                    )
                                }
                                size="small"
                                variant="outlined"
                                sx={{
                                    height: 24,
                                    fontSize: '10px',
                                    '& .MuiSelect-select': {
                                        py: 0.25,
                                        px: 1,
                                    },
                                }}
                            >
                                {CATEGORY_METRICS.map(m => (
                                    <MenuItem
                                        key={m.key}
                                        value={m.key}
                                        sx={{ fontSize: '11px' }}
                                    >
                                        {m.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </PanelHeader>
                        <Box sx={{ p: 1.5 }}>
                            {loading ? (
                                [...Array(5)].map((_, i) => (
                                    <Skeleton
                                        key={i}
                                        height={18}
                                        sx={{ mb: 1 }}
                                    />
                                ))
                            ) : visibleCategoryStats.length > 0 ? (
                                visibleCategoryStats.map(stat => (
                                    <CategoryRow
                                        key={stat.category}
                                        stat={stat}
                                        metric={categoryMetric}
                                        maxAbs={maxAbs}
                                    />
                                ))
                            ) : (
                                <Typography
                                    sx={{
                                        fontSize: '11px',
                                        color: 'text.disabled',
                                    }}
                                >
                                    Category breakdown coming soon.
                                </Typography>
                            )}
                        </Box>
                    </SectionCard>

                    <SectionCard>
                        <Tooltip title={timingInsight} placement="top" arrow>
                            <Box>
                                <PanelHeader
                                    title="Trade timing (UTC hour)"
                                    icon="🕐"
                                />
                            </Box>
                        </Tooltip>
                        <Box sx={{ p: 1.5 }}>
                            {loading ? (
                                <Skeleton
                                    height={110}
                                    variant="rectangular"
                                    sx={{ borderRadius: 1 }}
                                />
                            ) : profile ? (
                                <SvgBarChart
                                    values={profile.activity.hourly_activity}
                                    labels={profile.activity.hourly_activity.map(
                                        (_, i) => String(i)
                                    )}
                                    tooltipLabels={profile.activity.hourly_activity.map(
                                        (_, i) =>
                                            `${String(i).padStart(2, '0')}:00 – ${String((i + 1) % 24).padStart(2, '0')}:00 UTC`
                                    )}
                                    tooltipKeyLabel="Time"
                                    tooltipCountLabel="Trades"
                                    colors={hourlyColors}
                                    height={110}
                                />
                            ) : null}
                        </Box>
                    </SectionCard>

                    <SectionCard>
                        <Tooltip title={sizeInsight} placement="top" arrow>
                            <Box>
                                <PanelHeader
                                    title="Position size distribution"
                                    icon="📦"
                                />
                            </Box>
                        </Tooltip>
                        <Box sx={{ p: 1.5 }}>
                            {loading ? (
                                <Skeleton
                                    height={110}
                                    variant="rectangular"
                                    sx={{ borderRadius: 1 }}
                                />
                            ) : profile ? (
                                <SvgBarChart
                                    values={profile.activity.size_distribution.map(
                                        b => b.count
                                    )}
                                    labels={profile.activity.size_distribution.map(
                                        b => b.bucket
                                    )}
                                    tooltipKeyLabel="Amount"
                                    tooltipCountLabel="Trades"
                                    height={110}
                                />
                            ) : null}
                        </Box>
                    </SectionCard>
                </Box>

                {/* ── Positions table ── */}
                <SectionCard id="positions" sx={{ mb: 1.5 }}>
                    <PanelHeader title="Positions" icon="📋">
                        {heldWindowActive && (
                            <Chip
                                label={heldWindowLabel}
                                size="small"
                                onDelete={() => setHeldWindowOff(true)}
                                onClick={() => setHeldWindowOff(true)}
                                sx={{
                                    height: 22,
                                    fontSize: '10px',
                                    fontWeight: 600,
                                    '& .MuiChip-deleteIcon': {
                                        fontSize: '14px',
                                    },
                                }}
                            />
                        )}
                    </PanelHeader>
                    <PositionsTable
                        address={address}
                        pageSize={25}
                        availableCategories={availableCategories}
                        heldFrom={
                            heldWindowActive
                                ? zoomDomain![0].toISOString()
                                : null
                        }
                        heldTo={
                            heldWindowActive
                                ? zoomDomain![1].toISOString()
                                : null
                        }
                    />
                </SectionCard>
                <SimilarTradersPanel address={address} />
            </Box>

            {/* Right gutter */}
            <Box
                sx={{
                    display: { xs: 'none', lg: 'block' },
                    borderLeft: 1,
                    borderColor: 'divider',
                }}
            />
        </Box>
    );
}
